import type { Due, Item, ItemPatch, List } from "../lib/api.js";
import { el } from "../lib/dom.js";
import { itemFields } from "./itemForm.js";
import { formatDue } from "../lib/format.js";
import { renderNote } from "../lib/markdown.js";
import type { WriteGate } from "./listPolicy.js";
import { actionRow, offersDue, offersQuantity, paintAttribution, type DraftGuard, type ScreenHost } from "./ui.js";

/**
 * The panel for one item, wherever the row was selected from.
 *
 * It moved out of `views/list.ts` when the projections arrived: a row in Today
 * belongs to some other list, so a panel closure-bound to *the screen's* list
 * would offer the wrong fields and save against the wrong kind. It takes the
 * item's own list instead, which the caller looks up.
 *
 * The shell decides where this lands — a sheet, a modal or the inspector
 * column — and this does not know which. What it does not do is take the row's
 * place: the row stays put, so ticking or reading the rows around the one
 * being edited still works.
 *
 * It is the item's *data* and nothing else: the fields, Save and Cancel (g18).
 * Move up and Move down, Insert above and below, Open list and Delete used to
 * sit under the footer here, and made one tall sheet of two different jobs.
 * They are the row's actions menu's now (`views/menu.ts`, opened from the
 * ellipsis), where they are reached without opening a form, and the form is
 * left to be a short, focused task.
 */

export interface EditorHandlers {
  /** The person is done with the panel: undo the navigation that opened it. */
  onDismiss(): void;
  /** After every write — with an error, or with nothing when it took. */
  onResult(error?: unknown): void;
  /**
   * Who added the item, on a shared list (g72): "Added by Alex", or
   * undefined where the list is nobody else's business. Read again on
   * `repaint`, since a name can change while the panel is open.
   */
  attribution?(): string | undefined;
  /**
   * Where Save passes on a shared list (g72): a link's editor with no name
   * is asked for one, and the save runs once they have given it.
   */
  gate?: WriteGate;
}

export interface Editor {
  node: HTMLFormElement;
  /** What the shell asks before throwing away a changed draft (E05). */
  guard: DraftGuard;
  /** Re-reads what is said about the item, never the form. */
  repaint(): void;
}

/** The question, in the spec's words. Exported so the tests pin them. */
export const DISCARD_EDITS = {
  title: "Discard changes?",
  message: "Your edits to this item will be lost.",
  keep: "Keep editing",
  discard: "Discard changes",
};

function sameDue(a: Due | null, b: Due | null): boolean {
  if (a === null || b === null) return a === b;
  return a.date === b.date && a.time === b.time && a.tz === b.tz;
}

export function editorPanel(
  shell: ScreenHost,
  item: Item,
  list: List,
  handlers: EditorHandlers,
): Editor {
  const { store } = shell;
  const { onDismiss, onResult } = handlers;

  const fields = itemFields("detail", item, shell.announce);

  // Two groups, as Add has them: what the item is — its text and, where
  // the kind offers one, its quantity — then what is said about it and when
  // (D02). Which optional fields appear is the list's kind's call, unless
  // the item already holds a value — a field that vanishes is a value nobody
  // can see or correct (D05). Everything the item holds is on screen: Edit
  // is where a note or a schedule is changed, so none of it is folded away.
  const layout = [
    el("div", { class: "form-group", children: [
      fields.rows.item,
      offersQuantity(list.kind, item) && fields.rows.quantity,
      fields.rows.category,
    ] }),
    el("div", { class: "form-group", children: [
      fields.rows.note,
      offersDue(list.kind, item) && fields.rows.due,
      offersDue(list.kind, item) && fields.rows.repeat,
    ] }),
  ];

  /**
   * What the form holds, read against what was last saved.
   *
   * `base` moves on with every write this panel makes, because the panel
   * outlives its writes: Save closes through Back on a phone, which lands
   * later, and an Escape in that gap must not ask about edits that already
   * took. Every reader — submit and the shell's guard — goes through this
   * one reading, so none can disagree about what "changed" means.
   */
  let base: Item = item;

  function draft(): { patch: ItemPatch } {
    const patch: ItemPatch = {};
    const next = fields.read();
    if (next.text !== base.text) patch.text = next.text;

    if (offersQuantity(list.kind, item) && next.quantity !== base.quantity) patch.quantity = next.quantity;
    // `?? null`: a row from before g79 has no category, which is none, not a change.
    if (next.category !== (base.category ?? null)) patch.category = next.category;
    if (next.note !== base.note) patch.note = next.note;

    if (offersDue(list.kind, item)) {
      if (!sameDue(next.due, base.due)) patch.due = next.due;
      if (next.repeat !== (base.repeat ?? null)) patch.repeat = next.repeat;
    }

    return { patch };
  }

  /**
   * The draft, settled: saved when it changed, refused when it is blank,
   * nothing when it is neither. True when the form may close.
   *
   * An empty patch queues nothing, and rightly: it would bump `updatedAt`
   * and push a row that says nothing into every other device's delta.
   */
  function resolve(): boolean {
    const { patch } = draft();
    // A blank Item is refused, not ignored (D04): the old path dropped the
    // title from the patch and saved the rest, which read as a save that took
    // and left the row named what it was. A date or time only partly typed
    // reads as empty and would save as no schedule at all, so it is refused
    // beside its field too.
    const invalid = fields.validate();
    if (invalid) {
      invalid.focus();
      return false;
    }
    if (Object.keys(patch).length === 0) return true;
    try {
      store.patchItem(base.id, patch);
      base = { ...base, ...patch };
      onResult();
      return true;
    } catch (error: unknown) {
      onResult(error);
      return false;
    }
  }

  // Above the fields, and outside them: who added an item is not something
  // this form edits (g72). Written as text, so a name change repaints it
  // without touching a field.
  const attribution = el("p", { class: "muted editor-attribution" });
  const repaint = (): void => paintAttribution(attribution, handlers.attribution?.());
  repaint();

  const form = el("form", {
    class: "editor item-form",
    attrs: { novalidate: true },
    children: [
      attribution,
      ...layout,
      // The footer: the way out of the form, then what it is for.
      actionRow(
        [
          el("button", {
            class: "secondary",
            text: "Cancel",
            attrs: { type: "button" },
            // Through the host, which asks about a changed draft first (E05).
            on: { click: () => shell.detail.dismiss() },
          }),
        ],
        el("button", { text: "Save", attrs: { type: "submit" } }),
        "editor-actions",
      ),
    ],
    on: {
      submit: (event) => {
        event.preventDefault();
        const { gate } = handlers;
        if (!gate || gate.ready()) {
          if (resolve()) onDismiss();
          return;
        }
        // Checked before asking: a blank or half-typed field is said now,
        // not after a name.
        const invalid = fields.validate();
        if (invalid) {
          invalid.focus();
          return;
        }
        gate.hold({
          // Only while the panel is still this one's: the draft is read at
          // the moment the name is given.
          run: () => { if (form.isConnected && resolve()) onDismiss(); },
          valid: () => store.item(base.id) !== undefined,
          returnFocusTo: () => form.querySelector<HTMLElement>('button[type="submit"]') ?? undefined,
        });
      },
    },
  });

  // A form someone is filling in is not redrawn under them. It is closed by
  // them, or by the item going away — and the screen handles that.
  return {
    node: form,
    repaint,
    guard: {
      // A blank Item counts too: it is in the patch as a changed text, and
      // one the person may have been about to fix.
      // A half-typed date is a change too, though it reads as no date at all.
      dirty: () => Object.keys(draft().patch).length > 0 || fields.unfinished(),
      ...DISCARD_EDITS,
    },
  };
}

/** A note's `#` in Details: one below the panel's own `h2` title (`shell.ts`). */
const NOTE_HEADING_LEVEL = 3;

/**
 * Details for a view link's visitor (g72): everything the item holds, as
 * text, and a way out. No field and no Save: there is nothing here the link
 * may change. Text rather than disabled fields, so it keeps the contrast of
 * text and every word can be selected and copied. The note is rendered from
 * its markdown (g76); every other fact is plain text.
 */
export function readOnlyDetails(
  item: Item,
  list: List,
  handlers: {
    attribution?(): string | undefined;
    /** The item and its list as the store has them now, for a repaint after someone else's edit. */
    latest?(): { item: Item; list: List } | undefined;
    onClose(): void;
  },
): { node: HTMLElement; repaint(): void } {
  const attribution = el("p", { class: "muted editor-attribution" });
  const facts = el("dl", { class: "item-facts" });
  /** Each fact's row as drawn, by its term, with the value it shows. */
  const drawn = new Map<string, { value: string; node: HTMLElement }>();
  const factRow = (term: string, value: string): HTMLElement =>
    el("div", {
      class: "item-fact",
      children: [
        el("dt", { text: term }),
        term === "Note"
          ? el("dd", { class: "item-fact-note", children: [renderNote(value, { headingLevel: NOTE_HEADING_LEVEL })] })
          : el("dd", { text: value }),
      ],
    });
  const paintFacts = (shown: Item, of: List): void => {
    const pairs: [string, string | null | undefined][] = [
      ["Item", shown.text],
      ["Quantity", offersQuantity(of.kind, shown) ? shown.quantity : null],
      ["Category", shown.category],
      ["Note", shown.note],
      ["Due", shown.due ? formatDue(shown.due) : null],
      ["Repeats", shown.repeat ? shown.repeat : null],
      ["Status", shown.done ? "Done" : "Not done"],
    ];
    const present = pairs.filter((pair): pair is [string, string] => Boolean(pair[1]));
    // Only the facts that changed are replaced, and the rest are never taken
    // out: a redraw would clear a selection someone made to copy a fact, and
    // since g76 the note holds links a visitor may be on, and removing a
    // focused node drops the focus to the page. A repaint that changed
    // nothing touches nothing.
    const wanted = present.map(([term, value]) => {
      const kept = drawn.get(term);
      if (kept?.value === value) return kept.node;
      const node = factRow(term, value);
      drawn.set(term, { value, node });
      return node;
    });
    for (const child of [...facts.children]) if (!wanted.includes(child as HTMLElement)) child.remove();
    for (const term of [...drawn.keys()]) if (!present.some(([shownTerm]) => shownTerm === term)) drawn.delete(term);
    // The terms keep their order, so the rows kept are already in it and
    // only a new row is inserted before whatever follows it.
    wanted.forEach((node, index) => {
      if (facts.children[index] !== node) facts.insertBefore(node, facts.children[index] ?? null);
    });
  };
  // Someone else may change the item while it is open: the facts follow.
  const repaint = (): void => {
    const now = handlers.latest?.();
    paintFacts(now?.item ?? item, now?.list ?? list);
    paintAttribution(attribution, handlers.attribution?.());
  };
  repaint();
  const node = el("div", {
    class: "editor item-details",
    children: [
      attribution,
      facts,
      actionRow([el("button", { class: "secondary", text: "Close", attrs: { type: "button" }, on: { click: handlers.onClose } })], undefined, "editor-actions"),
    ],
  });
  return { node, repaint };
}
